"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "@/i18n/context";
import { XtorageLogo } from "@/components/brand/XtorageLogo";
import { BRAND_DOMAIN, BRAND_URL } from "@/lib/brand";
import { 
  Zap, 
  Cpu, 
  Globe, 
  ArrowRight, 
  CheckCircle2, 
  ShieldCheck, 
  BarChart3,
  Box,
  Truck
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { PlaceHolderImages } from "@/lib/placeholder-images";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

export default function LandingPage() {
  const { t, language, setLanguage } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const defaultHero = PlaceHolderImages.find(img => img.id === 'warehouse-hero');

  /**
   * ESTA PORTADA ES DE X-CARGO, NO DE NINGUNA EMPRESA.
   *
   * QUE SUSTITUYE: leia el branding de `localStorage`. Eso hacia que la imagen
   * solo se viera en el MISMO navegador donde se habia configurado —desde otro
   * ordenador, o tras limpiar datos, volvian los textos por defecto—.
   *
   * Y con varias empresas era peor: `localStorage` es del NAVEGADOR, no de la
   * empresa. Configurar Exito y despues intel dejaba la portada con lo ultimo
   * que se hubiera guardado, fuera de quien fuera.
   *
   * No se sustituye por una consulta a la base porque aqui NO HAY SESION: nadie
   * ha entrado todavia, asi que no hay forma de saber de que empresa es quien
   * mira. Esta pagina es el escaparate de X-Cargo y usa sus textos.
   *
   * Lo que una empresa personaliza se ve DENTRO de su espacio: el logo en la
   * cabecera y su imagen en el tablero. Si algun dia cada cliente tiene su
   * propia direccion —`intel.xtorage.com` o `/e/intel`— esta pagina podra
   * saber de quien es y leer su marca; hoy no puede.
   */
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const content = {
    heroTitle: t.landing?.hero_title,
    heroSubtitle: t.landing?.hero_subtitle,
    heroImageUrl: defaultHero?.imageUrl,
    feature1Title: t.landing?.feature_1_title,
    feature1Desc: t.landing?.feature_1_desc,
    feature2Title: t.landing?.feature_2_title,
    feature2Desc: t.landing?.feature_2_desc,
    feature3Title: t.landing?.feature_3_title,
    feature3Desc: t.landing?.feature_3_desc,
  };

  return (
    <div className="min-h-screen bg-background technical-grid selection:bg-primary/30">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-background/60 backdrop-blur-xl border-b border-border/60 h-20">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <XtorageLogo href="/" variant="horizontal" size="xl" priority />
          
          <div className="hidden md:flex items-center gap-8">
            <NavLink href="#features">{t.landing?.nav_features || "Features"}</NavLink>
            <NavLink href="#solutions">{t.landing?.nav_solutions || "Solutions"}</NavLink>
            <NavLink href="#intelligence">{t.landing?.nav_intelligence || "Intelligence"}</NavLink>
          </div>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-xl text-muted-foreground hover:text-primary hover:bg-muted/40"
                >
                  <Globe className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass-panel border-primary/20">
                <DropdownMenuItem
                  onClick={() => setLanguage('en')}
                  className={cn(language === 'en' ? 'bg-primary/20 text-primary' : '', "font-mono text-xs uppercase")}
                >
                  [EN] English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('es')}
                  className={cn(language === 'es' ? 'bg-primary/20 text-primary' : '', "font-mono text-xs uppercase")}
                >
                  [ES] Español
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('zh')}
                  className={cn(language === 'zh' ? 'bg-primary/20 text-primary' : '', "font-mono text-xs uppercase")}
                >
                  [ZH] 简体中文
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link href="/login">
              <Button variant="ghost" className="font-mono text-xs uppercase tracking-widest hover:text-primary">
                {t.landing?.login || "Operator Login"}
              </Button>
            </Link>
            <Link href="/login">
              <Button className="bg-primary text-primary-foreground font-bold rounded-xl px-6 shadow-lg shadow-primary/20">
                {t.landing?.get_started || "Get Started"}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 animate-in slide-in-from-left duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              <Zap size={14} className="text-primary" />
              <span className="text-xs font-mono font-bold text-primary uppercase tracking-widest">
                v2.4 Core Online
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-headline font-bold leading-[1.1] tracking-tighter uppercase">
              {content.heroTitle}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
              {content.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/login">
                <Button size="lg" className="bg-primary text-primary-foreground font-bold h-14 px-10 rounded-2xl shadow-xl shadow-primary/20 text-lg group">
                  {t.landing?.get_started}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="h-14 px-10 rounded-2xl border-border font-bold text-lg hover:bg-muted/40">
                {t.landing?.request_demo || "Request Demo"}
              </Button>
            </div>
          </div>

          <div className="relative h-[600px] rounded-[3rem] overflow-hidden border border-border/60 shadow-2xl animate-in fade-in zoom-in duration-1000 delay-200">
            {content.heroImageUrl && (
              <Image 
                src={content.heroImageUrl} 
                alt="Warehouse" 
                fill 
                className="object-cover opacity-60"
                data-ai-hint="warehouse interior"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-tr from-background via-transparent to-transparent" />
            
            {/* Floating UI Elements */}
            <div className="absolute top-10 right-10 p-6 glass-panel rounded-3xl space-y-4 animate-bounce-slow">
               <div className="flex items-center gap-3">
                 <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                 <span className="text-xs font-mono font-bold uppercase">Node Alpha-04 Active</span>
               </div>
               <div className="h-1 w-32 bg-muted/40 rounded-full overflow-hidden">
                 <div className="h-full bg-primary w-3/4" />
               </div>
            </div>

            <div className="absolute bottom-10 left-10 p-6 glass-panel rounded-3xl space-y-2">
               <span className="text-xs font-mono text-muted-foreground uppercase">Fulfillment Velocity</span>
               <div className="text-3xl font-headline font-bold text-primary">+245%</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-32 px-6 bg-sunken/50">
        <div className="max-w-7xl mx-auto space-y-20">
          <div className="text-center space-y-4">
            <h2 className="text-xs font-mono font-bold text-primary uppercase tracking-[0.4em]">{t.landing?.features_label || "CORE CAPABILITIES"}</h2>
            <h3 className="text-3xl md:text-5xl font-headline font-bold uppercase tracking-tight">{t.landing?.features_title || "Designed for the Speed of Commerce"}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={Box} 
              title={content.feature1Title} 
              desc={content.feature1Desc} 
            />
            <FeatureCard 
              icon={Cpu} 
              title={content.feature2Title} 
              desc={content.feature2Desc} 
              active
            />
            <FeatureCard 
              icon={Globe} 
              title={content.feature3Title} 
              desc={content.feature3Desc} 
            />
          </div>
        </div>
      </section>

      {/* Intelligence Section */}
      <section id="intelligence" className="py-32 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div className="order-2 lg:order-1 relative group">
             <div className="absolute inset-0 bg-primary/20 rounded-[3rem] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
             <div className="relative bg-card rounded-[3rem] border border-border/60 p-8 shadow-2xl overflow-hidden">
                <div className="h-64 flex items-end gap-3 mb-8">
                  {[40, 70, 45, 90, 65, 80, 50, 95].map((h, i) => (
                    <div key={i} className="flex-1 bg-primary/20 border-t-2 border-primary rounded-t-lg transition-all duration-1000 group-hover:bg-primary/40" style={{ height: `${h}%` }} />
                  ))}
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Neural Optimization</span>
                    <Badge className="bg-primary/20 text-primary border-primary/20">STABLE</Badge>
                  </div>
                  <h4 className="text-2xl font-headline font-bold uppercase tracking-tight">AI Predictive Slotting</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Xtorage uses Gemini to analyze SKU velocity and physical warehouse coordinates, recommending placement that reduces operator travel by up to 35%.
                  </p>
                </div>
             </div>
          </div>
          
          <div className="order-1 lg:order-2 space-y-8">
            <h3 className="text-4xl md:text-6xl font-headline font-bold uppercase leading-none">{t.landing?.intelligence_title || "Built-in intelligence for scale."}</h3>
            <div className="space-y-6">
               <CheckItem text={t.landing?.check_1 || "Dynamic inventory re-balancing"} />
               <CheckItem text={t.landing?.check_2 || "Real-time carrier rate comparison"} />
               <CheckItem text={t.landing?.check_3 || "Multi-tenant isolation & data sovereignty"} />
               <CheckItem text={t.landing?.check_4 || "Integrated barcode & RFID protocols"} />
            </div>
            <Link href="/login">
              <Button variant="outline" className="h-12 px-8 rounded-xl border-border hover:bg-primary hover:text-primary-foreground font-bold transition-all">
                {t.landing?.explore_ai || "Explore AI Hub"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-6 border-t border-border/60 bg-sunken">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
          <div className="space-y-6 max-w-sm">
            <XtorageLogo variant="horizontal" size="lg" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Global fulfillment solutions for modern e-commerce. Scalable, secure, and powered by neural networks.
            </p>
            <a
              href={BRAND_URL}
              className="text-xs font-mono text-primary hover:underline"
            >
              {BRAND_DOMAIN}
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-16">
            <FooterCol title="Solutions" links={["Fulfillment", "Warehouse Map", "AI Slotting"]} />
            <FooterCol title="Platform" links={["API Blueprint", "Integrations", "Security"]} />
            <FooterCol title="Legal" links={["Privacy", "Terms", "Compliance"]} />
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-border/60 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs font-mono text-muted-foreground uppercase tracking-widest">
           <span>© 2026 {BRAND_DOMAIN}. All signals encrypted.</span>
           <div className="flex gap-6">
             <Link href="#" className="hover:text-primary transition-colors">LinkedIn</Link>
             <Link href="#" className="hover:text-primary transition-colors">Twitter</Link>
           </div>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ href, children }: { href: string, children: React.ReactNode }) {
  return (
    <Link href={href} className="text-xs font-mono uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-primary transition-colors">
      {children}
    </Link>
  );
}

function FeatureCard({ icon: Icon, title, desc, active }: any) {
  return (
    <div className={cn(
      "p-8 rounded-[2.5rem] border transition-all duration-500 group",
      active 
        ? "bg-primary/5 border-primary/20 shadow-[0_0_40px_rgba(245,96,2,0.1)]" 
        : "bg-card/40 border-border/60 hover:border-primary/40 hover:bg-primary/5"
    )}>
      <div className={cn(
        "w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-500",
        active ? "bg-primary text-primary-foreground" : "bg-muted/40 text-primary"
      )}>
        <Icon size={28} />
      </div>
      <h4 className="text-xl font-headline font-bold uppercase tracking-tight mb-4">{title}</h4>
      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

function CheckItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-4 group">
      <div className="w-6 h-6 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
        <CheckCircle2 size={14} className="text-primary group-hover:text-primary-foreground transition-colors" />
      </div>
      <span className="text-sm font-medium tracking-tight text-muted-foreground group-hover:text-foreground transition-colors">{text}</span>
    </div>
  );
}

function FooterCol({ title, links }: { title: string, links: string[] }) {
  return (
    <div className="space-y-6">
      <h5 className="text-xs font-mono font-bold text-primary uppercase tracking-widest">{title}</h5>
      <ul className="space-y-4">
        {links.map(l => (
          <li key={l}>
            <Link href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{l}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
